import type { ReactElement } from 'react';
import {
  Box, Typography, alpha, Stack, Paper, Chip, IconButton, Tooltip,
} from '@mui/material';
import { ArrowForward, Lock, ModeEdit, Delete } from '@mui/icons-material';
import { resolveServiceIcon } from '../../constants/serviceIcons';

export type HubServiceTileModel = {
  slug: string;
  title: string;
  subtitle: string;
  color: string;
  icon: string;
  hubLabel: string;
  ready: boolean;
  active: boolean;
  isSystem?: boolean;
};

type Props = {
  tile: HubServiceTileModel;
  onOpen: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
};

/** Compact professional service card for hub grid view */
export default function HubServiceSelectCard({ tile, onOpen, onEdit, onDelete }: Props) {
  const { color, ready, active, title, subtitle, icon, hubLabel, isSystem } = tile;

  return (
    <Paper
      elevation={0}
      onClick={() => ready && onOpen()}
      role={ready ? 'button' : undefined}
      tabIndex={ready ? 0 : undefined}
      onKeyDown={(e) => {
        if (ready && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(color, 0.18),
        bgcolor: 'background.paper',
        cursor: ready ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        overflow: 'hidden',
        '&:hover': ready
          ? {
              borderColor: alpha(color, 0.45),
              boxShadow: `0 10px 24px ${alpha(color, 0.14)}`,
              transform: 'translateY(-2px)',
            }
          : undefined,
        '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          height: 6,
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.45)})`,
        }}
      />
      <Box sx={{ p: 1.75 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.75,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.1),
              color,
              border: `1px solid ${alpha(color, 0.18)}`,
              '& svg': { fontSize: 22 },
            }}
          >
            {resolveServiceIcon(icon) as ReactElement}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                {title}
              </Typography>
              <Chip
                size="small"
                label={ready ? (active ? 'Active' : 'Off') : '—'}
                color={ready && active ? 'success' : 'default'}
                sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', flexShrink: 0 }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                mt: 0.5,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {isSystem ? (
              <Chip size="small" icon={<Lock sx={{ fontSize: '12px !important' }} />} label="Core" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            ) : (
              <Chip size="small" label="Custom" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            <Chip size="small" label={hubLabel} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          </Stack>
          <Stack direction="row" spacing={0.35} alignItems="center" onClick={(e) => e.stopPropagation()}>
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={onEdit} sx={{ width: 28, height: 28, border: 1, borderColor: 'divider' }}>
                  <ModeEdit sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete} sx={{ width: 28, height: 28, border: 1, borderColor: alpha('#d32f2f', 0.3) }}>
                  <Delete sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={ready ? `Open ${title}` : 'Unavailable'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!ready}
                  onClick={() => { if (ready) onOpen(); }}
                  sx={{
                    color: '#fff',
                    bgcolor: color,
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: alpha(color, 0.88) },
                    '&.Mui-disabled': { bgcolor: alpha('#0f172a', 0.08), color: 'text.disabled' },
                  }}
                >
                  <ArrowForward sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
